import { useEffect, useMemo, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api, errorText } from '../api/client'
import type { MergedStage, QueuePrefs, StagePrefs } from '../api/types'
import { Icon } from '../components/Icon'
import { ErrorState, Loading } from '../components/States'
import { GoogleCalendarCard } from '../components/GoogleCalendarCard'
import { ContactRoles } from '../components/ContactRoles'
import { useConfirm } from '../components/Confirm'
import { useToast } from '../components/Toast'
import { titleCase } from '../lib/format'
import { stageColor } from '../lib/tint'
import { useAsync } from '../lib/useAsync'

// Three settings, each answering one question, on separate tabs:
//   Pipeline board — what are the columns on my board called, and which do I see?
//   Today list     — what is allowed to show up on my Today screen, and when?
//   Calendar       — which Google Calendar my appointments sync with.
// They are unrelated, so they are never on screen together. (Automations moved to
// the Workflows page: they answer "how does a workflow start by itself".)

type Tab = 'pipeline' | 'today' | 'calendar' | 'roles'

/** Each Today alert, in words, with the one number that tunes it — kept on the
 *  same line so "on/off" and "after how long" read as one setting. */
const TODAY_ALERTS: Record<string, {
  title: string
  detail: string
  threshold?: { key: keyof Omit<QueuePrefs, 'disabled'>; before: string; after: string; min: number; max: number }
}> = {
  lock_expiring: { title: 'A rate lock is about to expire', detail: 'So you can extend it or close in time.',
    threshold: { key: 'lock_days', before: 'Warn me', after: 'days before it expires', min: 1, max: 60 } },
  task_overdue: { title: 'One of my tasks is overdue', detail: 'Anything on your task list past its due date.' },
  blocking_condition: { title: 'A loan is waiting on a document', detail: 'A condition was requested and hasn’t come in.' },
  clear_to_close: { title: 'A loan is clear to close', detail: 'Time to schedule the signing.' },
  task_due: { title: 'A task is due today', detail: 'From your task list, including ones workflows created.' },
  stalled: { title: 'A loan has stopped moving', detail: 'No stage change in a while.',
    threshold: { key: 'stall_days', before: 'After', after: 'days in the same stage', min: 3, max: 120 } },
  partner_quiet: { title: 'A referral partner has gone quiet', detail: 'Worth a call before they send business elsewhere.',
    threshold: { key: 'quiet_days', before: 'After', after: 'days without a new loan from them', min: 14, max: 365 } },
}

const slug = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_|_$/g, '').slice(0, 40)

export function Customize() {
  const [params, setParams] = useSearchParams()
  const requested = params.get('tab')
  // Coming back from Google's consent screen lands on ?calendar=…; open the tab
  // that message belongs to rather than dropping the officer on the pipeline.
  const outcome = params.get('calendar')
  const tab: Tab = requested === 'today' ? 'today'
    : requested === 'roles' ? 'roles'
    : requested === 'calendar' || outcome ? 'calendar' : 'pipeline'
  const { data, error, loading, reload } = useAsync(() => api.customize(), [])
  const [stagePrefs, setStagePrefs] = useState<StagePrefs>({})
  const [queue, setQueue] = useState<QueuePrefs | null>(null)
  const [dirty, setDirty] = useState(false)
  const [busy, setBusy] = useState(false)
  const [newStage, setNewStage] = useState({ label: '', rolls_up_to: '' })
  const toast = useToast()
  const confirm = useConfirm()

  useEffect(() => {
    if (!data) return
    setStagePrefs(data.prefs.stages ?? {})
    setQueue(data.queue)
    setDirty(false)
  }, [data])

  const vocab = useMemo(() => (data?.vocabulary ?? []).filter((s) => !s.terminal), [data])

  // The board as it will look with unsaved edits applied, in funnel order.
  const preview: MergedStage[] = useMemo(() => {
    if (!data) return []
    const labels = stagePrefs.labels ?? {}
    const hidden = new Set(stagePrefs.hidden ?? [])
    const base = vocab.map((s) => ({
      ...s, canonical_label: s.label, label: labels[s.key] ?? s.label, locked: true, custom: false,
      rolls_up_to: '', hidden: hidden.has(s.key),
    }))
    const orderOf = (k: string) => vocab.find((v) => v.key === k)?.order ?? 999
    const custom = (stagePrefs.custom ?? []).map((c) => ({
      key: c.key, label: labels[c.key] ?? c.label, canonical_label: c.label, order: c.order ?? orderOf(c.rolls_up_to) - 0.5,
      terminal: false, locked: false, custom: true, rolls_up_to: c.rolls_up_to, hidden: hidden.has(c.key),
    }))
    return [...base, ...custom].sort((a, b) => a.order - b.order)
  }, [data, stagePrefs, vocab])

  if (loading && !data) return <div className="page"><Loading /></div>
  if (error || !data || !queue) return <div className="page"><ErrorState error={error} onRetry={reload} /></div>

  const edit = (fn: (p: StagePrefs) => StagePrefs) => { setStagePrefs(fn); setDirty(true) }
  const editQueue = (fn: (q: QueuePrefs) => QueuePrefs) => { setQueue((q) => (q ? fn(q) : q)); setDirty(true) }

  function rename(key: string, label: string, canonical: string) {
    edit((p) => {
      const labels = { ...(p.labels ?? {}) }
      if (!label.trim() || label.trim() === canonical) delete labels[key]
      else labels[key] = label
      return { ...p, labels }
    })
  }

  function toggleHidden(key: string) {
    edit((p) => {
      const hidden = new Set(p.hidden ?? [])
      if (hidden.has(key)) hidden.delete(key)
      else hidden.add(key)
      return { ...p, hidden: [...hidden] }
    })
  }

  function addStage() {
    const label = newStage.label.trim()
    const parent = newStage.rolls_up_to || vocab[0]?.key
    if (!label || !parent) return
    const key = `user:${slug(label) || 'stage'}`
    if ((stagePrefs.custom ?? []).some((c) => c.key === key)) return toast('You already have a column with that name.', { tone: 'warning' })
    edit((p) => ({ ...p, custom: [...(p.custom ?? []), { key, label, rolls_up_to: parent }] }))
    setNewStage({ label: '', rolls_up_to: parent })
  }

  function removeStage(key: string) {
    edit((p) => ({
      ...p,
      custom: (p.custom ?? []).filter((c) => c.key !== key),
      hidden: (p.hidden ?? []).filter((h) => h !== key),
      labels: Object.fromEntries(Object.entries(p.labels ?? {}).filter(([k]) => k !== key)),
    }))
  }

  async function save() {
    setBusy(true)
    try {
      // Only non-empty names: the server rejects a blank column name.
      const labels = Object.fromEntries(Object.entries(stagePrefs.labels ?? {}).filter(([, v]) => v.trim()))
      await api.setPref('stages', { ...stagePrefs, labels })
      await api.setPref('queue', queue!)
      toast('Saved', { tone: 'positive' })
      reload()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  async function reset() {
    const what = tab === 'pipeline' ? 'your Pipeline board' : 'your Today list'
    if (!await confirm({ title: `Put ${what} back to the standard setup?`,
                        body: 'Your names, hidden stages and order go back to how they started.',
                        confirmLabel: 'Reset it', danger: true })) return
    setBusy(true)
    try {
      await api.resetPref(tab === 'pipeline' ? 'stages' : 'queue')
      toast('Back to the standard setup', { tone: 'positive' })
      reload()
    } catch (e) {
      toast(errorText(e), { tone: 'danger' })
    } finally {
      setBusy(false)
    }
  }

  const switchTab = async (t: Tab) => {
    if (dirty && !await confirm({
      title: 'You have unsaved changes',
      body: 'They stay on this tab until you leave the page — switch anyway?',
      confirmLabel: 'Switch tabs',
    })) return
    setParams(t === 'pipeline' ? {} : { tab: t }, { replace: true })
  }

  const alertKeys = Object.keys(data.queue_rules).filter((k) => TODAY_ALERTS[k])
  const onCount = alertKeys.filter((k) => !queue.disabled.includes(k)).length

  return (
    <div className="page" style={{ maxWidth: tab === 'roles' ? 1120 : 900 }}>
      <div className="page-head">
        <div>
          <h1 className="h1">Customize</h1>
          <p className="subline">
            {tab === 'roles'
              ? 'What a contact can be, and what the CRM does with each kind. These apply to the whole branch.'
              : 'Change how your screens look. These settings only affect you.'}
          </p>
        </div>
        {/* The calendar tab saves as you go — connecting is the save — so the
            page-level buttons would have nothing to act on there. */}
        {tab === 'calendar' || tab === 'roles' ? null : (
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn" onClick={reset} disabled={busy}>Reset this tab</button>
            <button className="btn primary" onClick={save} disabled={!dirty || busy}>
              {busy ? 'Saving…' : dirty ? 'Save changes' : 'Saved'}
            </button>
          </div>
        )}
      </div>

      <div className="tabs" role="tablist" style={{ marginBottom: 16 }}>
        <button role="tab" aria-selected={tab === 'pipeline'} className={`tab${tab === 'pipeline' ? ' active' : ''}`} onClick={() => switchTab('pipeline')}>
          Pipeline board
        </button>
        <button role="tab" aria-selected={tab === 'today'} className={`tab${tab === 'today' ? ' active' : ''}`} onClick={() => switchTab('today')}>
          Today list
        </button>
        <button role="tab" aria-selected={tab === 'calendar'} className={`tab${tab === 'calendar' ? ' active' : ''}`} onClick={() => switchTab('calendar')}>
          Calendar
        </button>
        <button role="tab" aria-selected={tab === 'roles'} className={`tab${tab === 'roles' ? ' active' : ''}`} onClick={() => switchTab('roles')}>
          Contact roles
        </button>
      </div>

      {tab === 'roles' ? (
        <ContactRoles isAdmin={data.is_admin} />
      ) : tab === 'calendar' ? (
        <GoogleCalendarCard outcome={outcome}
          onOutcomeSeen={() => setParams({ tab: 'calendar' }, { replace: true })} />
      ) : tab === 'pipeline' ? (
        <section className="card">
          <div className="card-head">
            <h2 className="h2">The columns on your Pipeline board</h2>
            <span className="spacer" />
            <Link to="/pipeline" className="quiet" style={{ fontSize: 12 }}>Open the board →</Link>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            <p className="quiet" style={{ marginTop: 0, fontSize: 12.5 }}>
              Rename a column to the word you use, hide ones you don’t need, or add your own. The standard
              columns come from the loan system, so they can be renamed and hidden but not deleted — and a column you
              add sits inside one of them, so branch reports still add up.
            </p>
            {preview.map((s) => (
              <div className="stagerow" key={s.key} style={{ opacity: s.hidden ? 0.5 : 1, paddingLeft: s.custom ? 22 : undefined }}>
                <span className="kswatch" style={{ width: 8, height: 8, background: stageColor(s.rolls_up_to || s.key, false) }} />
                <span style={{ flex: 1, minWidth: 0 }}>
                  <input className="field" value={stagePrefs.labels?.[s.key] ?? s.canonical_label}
                    onChange={(e) => rename(s.key, e.target.value, s.canonical_label)} maxLength={40}
                    aria-label={`Name for ${s.canonical_label}`} style={{ fontWeight: 600, fontSize: 13, padding: '4px 8px' }} />
                  <span style={{ fontSize: 11.5, color: 'var(--ink-3)', display: 'block', marginTop: 3, paddingLeft: 2 }}>
                    {s.custom
                      ? `Your column · counts as ${vocab.find((v) => v.key === s.rolls_up_to)?.label ?? titleCase(s.rolls_up_to)} in reports`
                      : s.hidden ? 'Hidden from your board'
                        : s.label !== s.canonical_label ? `Called “${s.canonical_label}” in LendingPad` : 'Standard column'}
                  </span>
                </span>
                <button className="btn sm" onClick={() => toggleHidden(s.key)} style={{ minWidth: 72 }}>
                  <Icon name={s.hidden ? 'visibility' : 'visibility_off'} size={15} /> {s.hidden ? 'Show' : 'Hide'}
                </button>
                {s.custom ? (
                  <button className="icon-btn-plain" style={{ marginLeft: 0 }} onClick={() => removeStage(s.key)} title="Delete this column" aria-label="Delete this column">
                    <Icon name="delete" size={18} />
                  </button>
                ) : <span style={{ width: 22 }} />}
              </div>
            ))}

            <div className="rule-builder" style={{ marginTop: 14 }}>
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 8 }}>Add a column of your own</div>
              <div className="rule-sentence">
                <input className="field" placeholder="e.g. House hunting" value={newStage.label} maxLength={40} style={{ width: 200 }}
                  onChange={(e) => setNewStage((n) => ({ ...n, label: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') addStage() }} aria-label="Column name" />
                <span>placed inside</span>
                <select className="field" value={newStage.rolls_up_to || vocab[0]?.key}
                  onChange={(e) => setNewStage((n) => ({ ...n, rolls_up_to: e.target.value }))} aria-label="Which standard column it sits in">
                  {vocab.map((v) => <option key={v.key} value={v.key}>{v.label}</option>)}
                </select>
                <button className="btn" onClick={addStage} disabled={!newStage.label.trim()}>Add</button>
              </div>
            </div>
          </div>
        </section>
      ) : (
        <section className="card">
          <div className="card-head">
            <h2 className="h2">What shows up on your Today list</h2>
            <span className="spacer" />
            <Link to="/" className="quiet" style={{ fontSize: 12 }}>Open Today →</Link>
          </div>
          <div className="card-body" style={{ paddingTop: 0 }}>
            <p className="quiet" style={{ marginTop: 0, fontSize: 12.5 }}>
              “Do this next” on your Today screen lists things that need you, most urgent first. Switch off anything you
              don’t want to be interrupted by — it will still show on the contact or loan itself. {onCount} of {alertKeys.length} are on.
            </p>
            {alertKeys.map((key) => {
              const a = TODAY_ALERTS[key]!
              const on = !queue.disabled.includes(key)
              return (
                <div className="stagerow" key={key} style={{ alignItems: 'flex-start', opacity: on ? 1 : 0.6 }}>
                  <span style={{ flex: 1, minWidth: 0 }}>
                    <span style={{ fontSize: 13, fontWeight: 600, display: 'block' }}>{a.title}</span>
                    <span style={{ fontSize: 12, color: 'var(--ink-3)' }}>{a.detail}</span>
                    {a.threshold && on ? (
                      <span className="rule-sentence" style={{ marginTop: 6, fontSize: 12.5 }}>
                        <span>{a.threshold.before}</span>
                        <input className="field mono" type="number" min={a.threshold.min} max={a.threshold.max}
                          value={queue[a.threshold.key]} style={{ width: 64, textAlign: 'center', padding: '3px 6px' }}
                          onChange={(e) => editQueue((q) => ({ ...q, [a.threshold!.key]: Number(e.target.value) }))}
                          aria-label={`${a.threshold.before} … ${a.threshold.after}`} />
                        <span>{a.threshold.after}</span>
                      </span>
                    ) : null}
                  </span>
                  <button className={`toggle ${on ? 'on' : 'off'}`} style={{ marginTop: 2 }}
                    onClick={() => editQueue((q) => ({ ...q, disabled: on ? [...q.disabled, key] : q.disabled.filter((d) => d !== key) }))}
                    aria-pressed={on} aria-label={on ? `Stop showing: ${a.title}` : `Show: ${a.title}`}>
                    <span className="knob" />
                  </button>
                </div>
              )
            })}
          </div>
        </section>
      )}

      <p className="quiet" style={{ fontSize: 12.5, marginTop: 14 }}>
        <Icon name="bolt" size={14} style={{ verticalAlign: -2 }} /> Looking for workflows that start by themselves?
        They’re on the <Link to="/workflows">Workflows</Link> page.
      </p>
    </div>
  )
}
